import * as d3 from 'd3';
import {HierarchyPointNode, TreeLayout} from 'd3-hierarchy';
import {BaseType, Selection} from 'd3-selection';
import {ZoomBehavior, ZoomedElementBaseType} from 'd3-zoom';
import {get} from 'lodash';
import {Subject} from 'rxjs';
import {v4 as generateUUID} from 'uuid';

import {BaseD3Selection, D3PaginationNode, D3TreeData, TreePointNode} from './d3.interface';
import {
  CIRCLE_RADIUS,
  HORIZONTAL_NODE_DISTANCE,
  INITIAL_LEFT_MARGIN,
  MAX_NODE_CHAR_COUNT,
  MAX_ZOOM,
  MIN_NODE_VERTICAL_SPACE,
  MIN_ZOOM,
  TRANSITION_DURATION,
  createCurvedPath
} from './d3.util';
import {DepthNodesCount} from './depth-child-count';

export abstract class D3Service<T extends D3TreeData<T>> {
  protected treeRoot: TreePointNode<T>;
  private paginationNumber = 10;
  private treeLayout: TreeLayout<T>;
  private selectedNodeId: string;
  public svgGroup: Selection<BaseType, T, HTMLElement, T>;
  public onLoadChildrenSubject: Subject<{node: T; skip: number; isMoreChildrenNeeded: boolean}> =
    new Subject();
  public onLabelLeftClickSubject: Subject<{nodeId: string; refModelId: string}> = new Subject();
  public onLabelRightClickSubject: Subject<{
    node: HierarchyPointNode<D3TreeData<T>>;
    isGroupingNode: boolean;
    isRootNode: boolean;
    event: MouseEvent;
  }> = new Subject();
  public onLabelHoverSubject: Subject<{
    node: HierarchyPointNode<D3TreeData<T>>;
    event: MouseEvent;
  }> = new Subject();
  public onLabelMouseLeaveSubject: Subject<any> = new Subject();

  onLoadChildren$ = this.onLoadChildrenSubject.asObservable();
  onLabelLeftClick$ = this.onLabelLeftClickSubject.asObservable();
  onLabelRightClick$ = this.onLabelRightClickSubject.asObservable();
  onLabelHover$ = this.onLabelHoverSubject.asObservable();
  onLabelMouseLeave$ = this.onLabelMouseLeaveSubject.asObservable();

  svg: Selection<BaseType, any, HTMLElement, any>;
  zoomListener: ZoomBehavior<ZoomedElementBaseType, any>;

  protected abstract appendNodeLabel(enterSelection: BaseD3Selection<T>): void;

  applyZoomableBehavior(svg, svgGroup: Selection<BaseType, any, HTMLElement, any>) {
    const zoom = () => {
      const {event} = d3;
      const {k, x, y} = event.transform;
      const transformValue = `translate(${x},${y})scale(${k})`;
      const toggleNodeEvent = get(event, 'sourceEvent.type') === 'click';

      if (toggleNodeEvent) {
        return svgGroup
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('transform', transformValue);
      }

      svgGroup.attr('transform', transformValue);
    };
    this.zoomListener = d3.zoom().scaleExtent([MIN_ZOOM, MAX_ZOOM]).on('zoom', zoom);

    svg.call(this.zoomListener);
  }

  appendSvgForTree({selector}) {
    const svg = d3
      .select<BaseType, T>(selector)
      .append('svg')
      .on('contextmenu', () => d3.event.preventDefault());
    const svgGroup = svg.append('g');

    this.svgGroup = svgGroup;
    this.svg = svg;

    return {svg, svgGroup};
  }

  setTreeLayout(size) {
    this.treeLayout = d3.tree<T>().size(size);
  }

  setNewTreeHeight() {
    const [oldHeight, width] = this.treeLayout.size();
    const totalNodeCount = new DepthNodesCount(this.treeRoot).getTotalNodeCount();
    const newHeight = totalNodeCount * MIN_NODE_VERTICAL_SPACE;

    if (oldHeight < newHeight) {
      this.treeLayout.size([newHeight, width]);
    }
  }

  setTreeRoot(treeJson, {collapseChildren = true, height, width}) {
    const root = d3.hierarchy(treeJson, d => {
      return d.kids;
    }) as TreePointNode<T>;

    root.data.kids = [...this.mapFetchedData(root.data, 'children', root)];

    root.x0 = height / 2;
    root.y0 = width / 2;

    if (collapseChildren && root.children) {
      root.children.forEach(d => this.collapseNodeAndChildren(d, {collapseChildren: true}));
    }

    this.treeRoot = root;
  }

  setStartPosition() {
    this.emitTransformEvent({x: INITIAL_LEFT_MARGIN});
  }

  updateTree(source = this.treeRoot) {
    this.setNewTreeHeight();

    const nodes = this.getAllExistingNodes();
    this.setParentNode(nodes);

    this.updateNodes(nodes, source);

    const links = nodes.slice(1);

    this.updateLinks(links, source);

    this.setOldPositions(nodes);

    this.applyPaginationSign(nodes);
    this.setNodeSelection(nodes);
  }

  private getAllExistingNodes() {
    return this.treeLayout(this.treeRoot).descendants() as TreePointNode<T>[];
  }

  private setParentNode(nodes: TreePointNode<T>[]) {
    const parentNode = nodes[0];

    if (!this.selectedNodeId) {
      this.selectedNodeId = parentNode.data._id;
    }
  }

  mapFetchedData(fetchedKidsData, arrayToMap: 'children' | 'kids', parentNode: TreePointNode<T>) {
    return fetchedKidsData[`${arrayToMap}`].map(child => {
      return this.transformToHierarchyNode(child, parentNode);
    });
  }

  private transformToHierarchyNode(child, parentNode) {
    const hierarchyChild = d3.hierarchy(child, d => d.children);
    hierarchyChild.parent = parentNode;
    // @ts-ignore
    hierarchyChild.depth = parentNode.depth + 1;

    return hierarchyChild;
  }

  reset(source: any = this.treeRoot) {
    if (source && source.children) {
      source.page = 1;
      source['kids'] = [];

      source.children.forEach((child, i) => {
        if (child.pageNo) {
          return;
        }

        child.pageNo = Math.ceil((i + 1) / this.paginationNumber);
        if (source.page === child.pageNo) {
          source['kids'].push(child);
        }
        this.reset(child);
      });
      return source;
    }
  }

  emitTransformEvent({x = 0, y = 100, k = 1}) {
    const zoomTransformInstance = d3.zoomTransform(this.svg.node() as ZoomedElementBaseType);

    this.zoomListener.transform(this.svg as any, zoomTransformInstance.translate(x, y).scale(k));
  }

  emitZoomEvent(scale) {
    this.svg.transition().call(this.zoomListener.scaleBy as any, scale);
  }

  private collapseNodeAndChildren(d, {collapseChildren} = {collapseChildren: false}) {
    if (d.children) {
      d._children = d.children;
      d._children.forEach(child => this.collapseNodeAndChildren(child, {collapseChildren}));
      d.children = null;
    }
  }

  private setOldPositions(nodes) {
    nodes.forEach(d => {
      d.x0 = d.x;
      d.y0 = d.y;
    });
  }

  private updateNodes(nodes, source) {
    this.setHorizontalNodeDistance(nodes);

    const nodeSelection: BaseD3Selection<T> = this.svgGroup
      .selectAll('g.node')
      .data(nodes, this.identifyByUUID);
    const sourceSelection = nodeSelection.filter(d => source === d);
    const nodeEnterSelection: BaseD3Selection<T> = nodeSelection.enter().append('g');
    const mergedNodeSelection = nodeSelection.merge(nodeEnterSelection);
    const collapseEffectedSelection = nodeEnterSelection.merge(sourceSelection);

    this.appendNode(nodeEnterSelection, source);
    this.appendNodeCircle(nodeEnterSelection);
    this.appendNodeLabel(nodeEnterSelection);

    this.applyCollapseSign(collapseEffectedSelection);
    this.removeCollapseSign(collapseEffectedSelection);

    this.applyNodeTransition(mergedNodeSelection);
    this.updateNodeStyles(mergedNodeSelection);

    this.removeExitingNodes(nodeSelection, source);
  }

  private setNodeSelection(nodes) {
    const nodeSelection: BaseD3Selection<T> = this.svgGroup
      .selectAll('g.node')
      .data(nodes, this.identifyByUUID);
    this.changeNodeLabelColor(nodeSelection);
  }

  private applyPaginationSign(nodes: TreePointNode<T>[]) {
    const parents = nodes.filter(d => {
      return d.data?.children.length > this.paginationNumber;
    });

    this.svgGroup.selectAll('.page').remove();

    parents.forEach(parent => {
      if (parent._children) {
        return;
      }

      const firstChild = parent.children[0];
      const lastChild = parent.children[parent.children.length - 1];
      const pagingData = this.getPagingData(parent);

      const pageControl = this.getPageControlSelection({
        pagingData,
        firstChild,
        lastChild
      });

      this.appendPaginationSigns(pageControl);
      this.appendPaginationInfo(pageControl, parent);
    });
  }

  private getPagingData(parentNode: TreePointNode<T>): D3PaginationNode<T>[] {
    const pagingData: D3PaginationNode<T>[] = [];

    if (parentNode.data.page > 1) {
      pagingData.push({
        type: 'prev',
        parent: parentNode,
        no: parentNode.data.page - 1
      } as D3PaginationNode<T>);
    }

    if (parentNode.data.page < Math.ceil(parentNode.data.children.length / this.paginationNumber)) {
      pagingData.push({
        type: 'next',
        parent: parentNode,
        no: parentNode.data.page + 1
      } as D3PaginationNode<T>);
    }

    return pagingData;
  }

  private getPageControlSelection({pagingData, firstChild, lastChild}) {
    return this.svgGroup
      .selectAll('.page')
      .data(pagingData, (d: D3TreeData<T>) => d.parent.data.uuid + d.type)
      .enter()
      .append('g')
      .attr('class', 'page')
      .attr('transform', (d: D3TreeData<T>) => {
        const x = d.type === 'next' ? firstChild.y : lastChild.y;
        const y = lastChild.x;
        return 'translate(' + x + ',' + y + ')';
      });
  }

  private appendPaginationSigns(pageControl: Selection<BaseType, unknown, BaseType, T>): void {
    pageControl
      .append('image')
      .attr('href', (d: D3TreeData<T>) => {
        if (d.type === 'next') {
          return '../../assets/icons/pagination-arrow-right.png';
        } else {
          return '../../assets/icons/pagination-arrow-left.png';
        }
      })
      .attr('x', (d: D3TreeData<T>) => {
        const amountOfPages = this.getAmountOfPages(d.parent);
        const amountOfNumbers = amountOfPages.toString().length;
        return d.type === 'next' ? 30 + amountOfNumbers * 3 : -30 - amountOfNumbers * 2;
      })
      .attr('y', 25)
      .attr('width', 15)
      .attr('height', 15)
      .on('click', (d: D3PaginationNode<T>) => this.handlePagination(d));
  }

  private appendPaginationInfo(nodeSelection, parent) {
    nodeSelection
      .append('text')
      .attr('class', 'paging-info')
      .append('tspan')
      .attr('x', () => {
        const amountOfNumbers = parent.data.page.toString().length;
        return -5 * amountOfNumbers;
      })
      .attr('y', 37)
      .transition()
      .text(() => {
        const pagesAmount = this.getAmountOfPages(parent);
        return `${parent.data.page}/${pagesAmount}`;
      });
  }

  private handlePagination(d: D3PaginationNode<T>) {
    d.parent.data.page = d.no;

    if (d.parent.data.children.length / this.paginationNumber - 2 === d.no) {
      this.fetchChildren(d.parent, d.parent.data.children.length, true);
    }

    this.setPage(d.parent);
    this.updateTree(d.parent);
  }

  private setPage(d: TreePointNode<T>): void {
    if (d && d.data.kids) {
      d.children = [];
      d.data.kids.forEach(d1 => {
        if (d.data.page === d1.data.pageNo) {
          d.children.push(d1);
        }
      });
    }
  }

  private getAmountOfPages(node) {
    return node.data.count
      ? Math.ceil(node.data.count / this.paginationNumber)
      : Math.ceil(node.data.children.length / this.paginationNumber);
  }

  private applyCollapseSign(selection: BaseD3Selection<T>) {
    const dy = navigator.userAgent.includes('Windows') ? '.36em' : '.27em';

    selection
      .filter(this.shouldShowCollapseSign)
      .append('text')
      .text('+')
      .attr('text-anchor', 'middle')
      .attr('dy', dy)
      .classed('circle-expand', true)
      .on('click', d => this.toggleNode(d));
  }

  private shouldShowCollapseSign(node: any) {
    return (
      (node.data.hasDependents && !node.children) ||
      (node.data.count && !node.children) ||
      !!node._children ||
      (node.data.count && !node.children)
    );
  }

  private removeCollapseSign(selection: BaseD3Selection<T>) {
    selection
      .select('text.circle-expand')
      .filter((d: any) => !this.shouldShowCollapseSign(d))
      .remove();
  }

  private setHorizontalNodeDistance(nodes) {
    nodes.forEach(d => (d.y = d.depth * HORIZONTAL_NODE_DISTANCE));
  }

  private appendNode(enterSelection: BaseD3Selection<T>, source) {
    enterSelection
      .attr('class', 'node')
      .attr('transform', () => `translate(${source.y0},${source.x0})`);
  }

  private appendNodeCircle(enterSelection: BaseD3Selection<T>) {
    enterSelection
      .append('circle')
      .attr('class', 'node')
      .attr('r', 1e-6)
      .style('fill', (d: any) =>
        this.shouldShowCollapseSign(d) ? 'var(--q9-primary-100)' : 'var(--q9-primary-100)'
      )
      .on('click', d => this.toggleNode(d));
  }

  private changeNodeLabelColor(enterSelection: BaseD3Selection<T>, nodeId = this.selectedNodeId) {
    const labelText = enterSelection.select('g.node text');
    this.setAllCirclesToDefaultColor();

    labelText.classed('node-selected', d => {
      if (!d.data.nodeId) {
        return d.data._id && d.data._id === nodeId;
      }

      return d.data.nodeId && d.data.nodeId === nodeId;
    });
  }

  private setAllCirclesToDefaultColor() {
    const nodes = this.getAllExistingNodes();

    const allLabels = this.svgGroup.selectAll('g.node text').data(nodes, this.identifyByUUID);

    allLabels.classed('node-selected', false);
  }

  public toggleNode(d) {
    const hasNoChildren = !d.children && !d._children;

    if ((d.data.count && hasNoChildren) || (d.data.hasDependents && hasNoChildren)) {
      this.fetchChildren(d);
    }

    if (hasNoChildren) {
      return;
    }

    if (d.children) {
      d._children = d.children;
      d.children = null;
    } else {
      d.children = d._children;
      d._children = null;
    }

    this.updateTree(d);
  }

  protected fetchChildren(node, skip = 0, isMoreChildrenNeeded = false) {
    this.onLoadChildrenSubject.next({node, skip, isMoreChildrenNeeded});
  }

  protected appendNodeLabelPlaceholder(enterSelection: BaseD3Selection<T>): BaseD3Selection<T> {
    const isMainParentNode = d => !d.parent;
    const onLabelLeftClick = ({nodeId, refModelId}) => {
      this.selectedNodeId = nodeId;
      this.changeNodeLabelColor(enterSelection);

      this.onLabelLeftClickSubject.next({nodeId, refModelId});
    };

    const onLabelRightClick = ({node, isGroupingNode = false, isRootNode = false}) => {
      this.onLabelRightClickSubject.next({node, isGroupingNode, isRootNode, event: d3.event});
    };

    const onLabelHover = node => this.onLabelHoverSubject.next({node, event: d3.event});

    const onLabelMouseLeave = () => this.onLabelMouseLeaveSubject.next(null);

    return enterSelection
      .append('text')
      .classed('node-label', true)
      .classed('dependency-node', node => {
        return (node.data.name && !node.data.type) || (node.data.name && !node.parent);
      })
      .on('click', node => {
        const nodeId = this.getNodeId(node);
        const refModelId = this.getRefModelId(node);

        if (nodeId && refModelId) {
          onLabelLeftClick({nodeId, refModelId});
        }
      })
      .on('contextmenu', node => {
        const nodeId = this.getNodeId(node);
        const refModelId = this.getRefModelId(node);
        const nodeType = this.getNodeType(node);

        if (nodeId && refModelId) {
          const rootNodeId = this.getNodeId(this.treeRoot);
          const isRootNode = nodeId === rootNodeId;

          onLabelRightClick({node, isRootNode});
        } else if (!nodeId && refModelId && nodeType) {
          onLabelRightClick({node, isGroupingNode: true});
        }
      })
      .on('mouseover', (node: TreePointNode<T>) => {
        if (node.data.relationDescription) {
          onLabelHover(node);
        }
      })
      .on('mouseout', () => onLabelMouseLeave())
      .attr('x', node => (isMainParentNode(node) ? -13 : 13))
      .attr('text-anchor', node => (isMainParentNode(node) ? 'end' : 'start'));
  }

  private getRefModelId(node: TreePointNode<T>): string {
    return node.data.refModelId;
  }

  private getNodeId(node: TreePointNode<T>): string {
    if (node.data.nodeId) {
      return node.data.nodeId;
    }
    if (node.data._id) {
      return node.data._id;
    }
  }

  private getNodeType(node: TreePointNode<T>) {
    return node.data.type;
  }

  protected clipText(text: string): string {
    if (text && text.length > MAX_NODE_CHAR_COUNT) {
      return `${text.slice(0, MAX_NODE_CHAR_COUNT - 3)}...`;
    } else {
      return text;
    }
  }

  private applyNodeTransition(mergedNodeSelection: BaseD3Selection<T>) {
    mergedNodeSelection
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('transform', d => `translate(${d.y},${d.x})`);
  }

  private updateNodeStyles(mergedNodeSelection: BaseD3Selection<T>) {
    mergedNodeSelection
      .select('circle.node')
      .attr('r', CIRCLE_RADIUS)
      .style('fill', (d: any) => {
        if (d.data.count) {
          return 'var(--q9-violet-100)';
        }
        return this.shouldShowCollapseSign(d) ? 'var(--q9-primary-100)' : 'var(--q9-primary-100)';
      })
      .attr('cursor', 'pointer');
  }

  private removeExitingNodes(nodeSelection: BaseD3Selection<T>, source) {
    const nodeExit = nodeSelection
      .exit()
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('transform', () => `translate(${source.y},${source.x})`)
      .remove();

    nodeExit.select('circle').attr('r', 1e-6);
    nodeExit.select('text').style('fill-opacity', 1e-6);
  }

  private updateLinks(links: HierarchyPointNode<T>[], source) {
    const link = this.svgGroup.selectAll('path.link').data(links, this.identifyByUUID);
    const linkEnterSelection: BaseD3Selection<T> = link
      .enter()
      .insert('path', 'g')
      .attr('class', 'link')
      .attr('d', () => {
        const o = {x: source.x0, y: source.y0};

        return createCurvedPath(o, o);
      });
    const linkMergedSelection = linkEnterSelection.merge(link);

    this.transitionToParentPosition(linkMergedSelection);
    this.removeExitingLinks(link, source);
  }

  private transitionToParentPosition(linkMergedSelection: BaseD3Selection<T>) {
    linkMergedSelection
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('d', d => createCurvedPath(d, d.parent));
  }

  private removeExitingLinks(link: BaseD3Selection<T>, source) {
    link
      .exit()
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('d', () => {
        const o = {x: source.x, y: source.y};

        return createCurvedPath(o, o);
      })
      .remove();
  }

  private identifyByUUID({data}: {data: T}) {
    return data.uuid ? data.uuid : (data.uuid = generateUUID());
  }
}
